import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { FaUser, FaEnvelope, FaLock, FaSpinner, FaCheckCircle, FaArrowLeft } from "react-icons/fa";
import "../../styles/AuthForm.css";

export default function RegisterForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Estados para verificación
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const navigate = useNavigate();
  const API_URL = "/backend";

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/register.php`,
        { name, email, password },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data.success) {
        // Guardar datos para la verificación
        setUserId(res.data.user_id);
        setUserEmail(res.data.email);
        
        // Mostrar pantalla de verificación
        setShowVerification(true);
      } else {
        setError(res.data.error || "Error al registrar");
      }
    } catch (err) {
      console.error(err);
      setError("Error de conexión con el servidor");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError("El código debe tener 6 dígitos");
      return;
    }

    setVerificationLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API_URL}/verificar_codigo.php`,
        { user_id: userId, codigo: verificationCode },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data.success) {
        setVerificationSuccess(true);
        
        // Redirigir al login después de 2 segundos
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(res.data.error || "Código incorrecto");
      }
    } catch (err) {
      console.error(err);
      setError("Error al verificar código");
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendDisabled(true);
    setResendTimer(60);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      await axios.post(`${API_URL}/enviar_codigo.php`,
        { user_id: userId, email: userEmail },
        { headers: { "Content-Type": "application/json" } }
      );
    } catch (err) {
      console.error("Error reenviando código:", err);
    }
  };

  // Pantalla de verificación
  if (showVerification) {
    return (
      <div className="auth-form-container">
        <div className="auth-form-header">
          <div className="auth-form-logo">
            <span className="auth-form-logo-icon">⚡</span>
            <span className="auth-form-logo-text">Reanimeitor</span>
          </div>
        </div>

        {verificationSuccess ? (
          <div className="verification-success">
            <FaCheckCircle className="success-icon" />
            <h3>¡Email verificado!</h3>
            <p>Redirigiendo al inicio de sesión...</p>
          </div>
        ) : (
          <>
            <h2 className="auth-form-title">Verifica tu email</h2>
            <p className="auth-form-subtitle">
              Hemos enviado un código a <strong>{userEmail}</strong>
            </p>

            {error && <div className="form-error-message">{error}</div>}

            <div className="verification-instructions">
              <p>Revisa tu bandeja de entrada (y spam). El código expira en 15 minutos.</p>
            </div>

            <div className="verification-code-input">
              <input
                type="text"
                maxLength="6"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                  setError("");
                }}
                className="code-input-field"
                autoFocus
                disabled={verificationLoading}
              />
            </div>

            <button 
              className="auth-submit-btn"
              onClick={handleVerifyCode}
              disabled={verificationCode.length !== 6 || verificationLoading}
            >
              {verificationLoading ? (
                <><FaSpinner className="spinning-icon" /> Verificando...</>
              ) : "Verificar código"}
            </button>

            <div className="verification-footer">
              <button 
                className="resend-btn"
                onClick={handleResendCode}
                disabled={resendDisabled}
              >
                {resendDisabled ? `Reenviar en ${resendTimer}s` : "Reenviar código"}
              </button>
              
              <button 
                className="back-btn"
                onClick={() => {
                  setShowVerification(false);
                  setVerificationCode("");
                  setError("");
                }}
              >
                <FaArrowLeft /> Volver al registro
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Formulario de registro normal
  return (
    <div className="auth-form-container">
      <div className="auth-form-header">
        <div className="auth-form-logo">
          <span className="auth-form-logo-icon">⚡</span>
          <span className="auth-form-logo-text">Reanimeitor</span>
        </div>
        <h2 className="auth-form-title">Crear Cuenta</h2>
        <p className="auth-form-subtitle">Únete a la revolución energética</p>
      </div>

      {error && <div className="form-error-message">{error}</div>}

      <form className="auth-form" onSubmit={handleRegister}>
        <div className="form-group">
          <div className="input-with-icon">
            <FaUser className="input-icon" />
            <input 
              type="text" 
              className="form-input" 
              placeholder="Nombre completo"
              required 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
            />
          </div>
        </div>

        <div className="form-group">
          <div className="input-with-icon">
            <FaEnvelope className="input-icon" />
            <input 
              type="email" 
              className="form-input" 
              placeholder="tu@email.com"
              required 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
            />
          </div>
        </div>

        <div className="form-group">
          <div className="input-with-icon">
            <FaLock className="input-icon" />
            <input 
              type="password" 
              className="form-input" 
              placeholder="••••••••"
              required 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
            />
          </div>
        </div>

        <button type="submit" className="auth-submit-btn" disabled={loading}>
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              Registrando...
            </div>
          ) : "Registrarse"}
        </button>
      </form>

      <div className="auth-footer">
        <p>
          ¿Ya tienes cuenta? <Link to="/login" className="auth-link">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}